import { useState } from "react";
import { Loader2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { shop_name: "", name: "", phone: "", place: "", pin: "1234", status: "approved" };
const input = "w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100";

export default function AddCustomerModal({ onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: k === "phone" || k === "pin" ? e.target.value.replace(/[^0-9]/g, "") : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.phone.length < 10) return setError("Enter a valid 10-digit mobile number.");
    if (form.pin.length < 4) return setError("PIN must be at least 4 digits.");
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success(`${form.shop_name} added. Login: ${form.phone} + PIN ${form.pin}`);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, extra = {}) => (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span>
      <input data-testid={`add-customer-${k}`} value={form[k]} onChange={set(k)} className={input} {...extra} />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <form data-testid="add-customer-modal" onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-bold text-blue-900"><UserPlus className="h-5 w-5" /> Add Customer</h3>
          <button type="button" data-testid="add-customer-close" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          {field("shop_name", "Shop Name *", { required: true })}
          {field("name", "Customer Name *", { required: true })}
          {field("phone", "Mobile Number *", { required: true, inputMode: "numeric", maxLength: 10 })}
          {field("place", "Village / Area")}
          {field("pin", "PIN *", { required: true, inputMode: "numeric" })}
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600">Status</span>
            <select data-testid="add-customer-status" value={form.status} onChange={set("status")} className={input}>
              <option value="approved">Approved / Active</option>
              <option value="pending">Pending</option>
            </select>
          </label>
          <p className="text-xs text-slate-500">Customer logs in on the Customer tab with this mobile number and PIN (default 1234).</p>
          {error && <p data-testid="add-customer-error" className="text-sm font-medium text-red-600">{error}</p>}
        </div>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button data-testid="add-customer-save" type="submit" disabled={saving}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-700 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-700/25 hover:bg-blue-800 disabled:opacity-70">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Customer"}
          </button>
        </div>
      </form>
    </div>
  );
}
